import { RefreshCw } from "lucide-react";
import { PlanActualsSummary } from "@/components/plans/PlanActualsSummary";
import { PlanActualsByMonth, PlanActualsTable } from "@/components/plans/PlanActualsTable";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { usePlanActuals, useRefreshPlanActuals } from "@/hooks/use-plan-actuals";
import type { PlanActualsView } from "@/schemas/plan-actuals";

function asOfLabel(view: PlanActualsView): string {
  if (view.asOf === null) return "Actuals not yet pulled from NOS";
  const when = new Date(view.asOf).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
  return `Actuals as of ${when}`;
}

/**
 * Resource Plan ▸ Actuals — what was planned against what was logged in NOS, and what that
 * did to margin. Shown on Won, Staffed and Active plans (see planShowsActuals).
 */
export function PlanActualsPanel({ planId }: { planId: string }) {
  const query = usePlanActuals(planId, true);
  const refresh = useRefreshPlanActuals(planId);

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (query.isError) {
    return (
      <ErrorState
        message={query.error instanceof Error ? query.error.message : "Couldn't load actuals"}
        onRetry={() => query.refetch()}
      />
    );
  }
  const view = query.data;
  if (view === undefined) return null;

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="text-sm text-muted-foreground">
        {asOfLabel(view)}
        {view.windowMonths.length > 0 &&
          ` · compared through ${view.includesPartialMonth ? "this month to date" : "the plan's last month"}`}
      </div>
      <Button
        variant="outline"
        disabled={refresh.isPending || view.linkedDemandCount === 0}
        onClick={() => refresh.mutate()}
        title="Pull the latest time entries for this Solution from NOS"
      >
        <RefreshCw className={refresh.isPending ? "animate-spin" : undefined} />
        Refresh
      </Button>
    </div>
  );

  if (view.linkedDemandCount === 0) {
    return (
      <div className="flex flex-col gap-3">
        {header}
        <div className="rounded-md border border-border bg-card px-4 py-6 text-sm text-muted-foreground">
          This Solution isn't linked to a NOS Demand yet, so no time can arrive for it. Once it's
          linked, actual hours appear here.
        </div>
      </div>
    );
  }
  if (view.windowMonths.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        {header}
        <div className="rounded-md border border-border bg-card px-4 py-6 text-sm text-muted-foreground">
          This plan hasn't started yet, so there's nothing to compare.
        </div>
      </div>
    );
  }

  const noTime = Number(view.summary.actualHours) === 0;
  return (
    <div className="flex flex-col gap-4">
      {header}
      {noTime && (
        <div className="rounded-md border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
          No time has been logged against this Solution in NOS for the plan's months yet.
        </div>
      )}
      <PlanActualsSummary view={view} />
      <PlanActualsTable view={view} />
      <PlanActualsByMonth view={view} />
    </div>
  );
}
